import {UserInformation} from 'components/types'

const STORAGE_PREFIX = 'dade.pending-admin-submission.v2'
const MAX_AGE_MS = 10 * 60 * 1000
const ACTION = 'create_admin'

type StoredAdminSubmission = {
  ownerUid: string
  action: typeof ACTION
  challengeId: string
  targetHash: string
  createdAt: number
  expiresAt: number
  formValues: UserInformation
}

const getSessionStorage = () => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

const getStorageKey = (ownerUid: string) =>
  `${STORAGE_PREFIX}:${encodeURIComponent(ownerUid)}`

const hasValidFormValues = (value: unknown): value is UserInformation => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const formValues = value as Record<string, unknown>
  return [
    'firstName',
    'lastName',
    'mobileNumber',
    'emailAddress',
    'photoURL',
    'role'
  ].every(
    (field) =>
      formValues[field] === undefined || typeof formValues[field] === 'string'
  )
}

const createChallengeId = () => {
  if (typeof window === 'undefined') return ''
  if (typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    ''
  )
}

const hashSubmission = async (submission: {
  ownerUid: string
  action: string
  challengeId: string
  createdAt: number
  expiresAt: number
  formValues: UserInformation
}) => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) return ''

  const canonical = JSON.stringify({
    ownerUid: submission.ownerUid,
    action: submission.action,
    challengeId: submission.challengeId,
    createdAt: submission.createdAt,
    expiresAt: submission.expiresAt,
    formValues: submission.formValues
  })
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonical)
  )
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
}

export const savePendingAdminSubmission = async (
  ownerUid: string,
  formValues: UserInformation
) => {
  const storage = getSessionStorage()
  if (!storage || !ownerUid || !hasValidFormValues(formValues)) return false

  const createdAt = Date.now()
  const pending = {
    ownerUid,
    action: ACTION as typeof ACTION,
    challengeId: createChallengeId(),
    createdAt,
    expiresAt: createdAt + MAX_AGE_MS,
    formValues
  }
  const targetHash = await hashSubmission(pending)
  if (!pending.challengeId || !targetHash) return false

  const submission: StoredAdminSubmission = {...pending, targetHash}
  storage.setItem(getStorageKey(ownerUid), JSON.stringify(submission))
  return true
}

export const loadPendingAdminSubmission = async (
  ownerUid: string
): Promise<UserInformation | null> => {
  const storage = getSessionStorage()
  if (!storage || !ownerUid) return null

  const key = getStorageKey(ownerUid)
  try {
    const stored = JSON.parse(
      storage.getItem(key) || 'null'
    ) as StoredAdminSubmission | null
    const validEnvelope =
      stored &&
      stored.ownerUid === ownerUid &&
      stored.action === ACTION &&
      typeof stored.challengeId === 'string' &&
      Boolean(stored.challengeId) &&
      Number.isFinite(stored.createdAt) &&
      Number.isFinite(stored.expiresAt) &&
      stored.expiresAt > Date.now() &&
      stored.expiresAt - stored.createdAt <= MAX_AGE_MS &&
      hasValidFormValues(stored.formValues)

    if (!validEnvelope) {
      storage.removeItem(key)
      return null
    }

    const targetHash = await hashSubmission(stored)
    if (!targetHash || targetHash !== stored.targetHash) {
      storage.removeItem(key)
      return null
    }

    return stored.formValues
  } catch {
    storage.removeItem(key)
    return null
  }
}

export const clearPendingAdminSubmission = (ownerUid?: string) => {
  const storage = getSessionStorage()
  if (!storage) return

  if (ownerUid) {
    storage.removeItem(getStorageKey(ownerUid))
    return
  }

  const keys = Array.from({length: storage.length}, (_, index) =>
    storage.key(index)
  ).filter((key): key is string =>
    Boolean(key?.startsWith(`${STORAGE_PREFIX}:`))
  )
  keys.forEach((key) => storage.removeItem(key))
}
